/**
 * Sanitize error messages before displaying to users.
 * Strips backend tech stack names that should never be user-facing.
 */

const FRIENDLY_BUSY = 'AI service is temporarily busy. Please try again in a moment.';
const FRIENDLY_UNSUPPORTED = "This file format isn't supported for analysis. Try a PDF, Word, or text file.";

const BUSY_TRIGGERS = /\b(429|resource[\s_]*exhausted|throttlingexception|quota[\s_]*exceeded|rate[\s_]*limit|too[\s_]*many[\s_]*requests)\b/i;
const UNSUPPORTED_MIMETYPE_TRIGGERS = /\bmime[\s_-]*type\b.*\b(not\s+supported|unsupported|invalid)\b|\b(not\s+supported|unsupported)\b.*\bmime[\s_-]*type\b/i;

const URL_STRIP = /https?:\/\/(?:cloud\.google\.com|[\w.-]*\.?anthropic\.com|[\w.-]*\.?amazon(?:aws)?\.com|[\w.-]*\.?openai\.com|[\w.-]*\.?googleapis\.com)\/\S*/gi;

// Strip dangling tails left after URL removal: "Learn more:", "See:", etc.
const DANGLING_TAILS = /\s*(?:learn\s+more|see\s+(?:more|details|here)?|more\s+info(?:rmation)?|details(?:\s+at)?|reference)\s*[:.]?\s*$/i;

const TECH_PATTERNS = [
  // AI/ML providers
  { pattern: /\bvertex\s*ai\b/gi, replacement: 'AI service' },
  { pattern: /\bgoogle\s*cloud\b/gi, replacement: 'AI service' },
  { pattern: /\bgoogle\.generativeai\b/gi, replacement: 'AI service' },
  { pattern: /\bgoogle\s*ai\b/gi, replacement: 'AI service' },
  { pattern: /\bgemini\b/gi, replacement: 'AI service' },
  { pattern: /\bclaude\b/gi, replacement: 'AI' },
  { pattern: /\banthropic\b/gi, replacement: 'AI provider' },
  { pattern: /\bopenai\b/gi, replacement: 'AI service' },
  { pattern: /\bgpt[-\s]?\d*/gi, replacement: 'AI model' },
  { pattern: /\bbedrock\b/gi, replacement: 'AI service' },
  { pattern: /\btitan\b/gi, replacement: 'AI model' },
  { pattern: /\bsonnet\b/gi, replacement: 'AI model' },
  { pattern: /\bopus\b/gi, replacement: 'AI model' },
  { pattern: /\bhaiku\b/gi, replacement: 'AI model' },
  // 3D/CAD APIs
  { pattern: /\bzoo\s*(api|service|ml)?\b/gi, replacement: '3D service' },
  { pattern: /\bkittycad\b/gi, replacement: '3D service' },
  { pattern: /\bforge\b/gi, replacement: 'viewer service' },
  // AWS services
  { pattern: /\baws\s+bedrock\b/gi, replacement: 'AI service' },
  { pattern: /\baws\s+cognito\b/gi, replacement: 'authentication service' },
  { pattern: /\bcognito\b/gi, replacement: 'authentication service' },
  { pattern: /\baws\s+s3\b/gi, replacement: 'storage service' },
  { pattern: /\bapp\s*runner\b/gi, replacement: 'server' },
  // Frameworks
  { pattern: /\bfastapi\b/gi, replacement: 'server' },
  { pattern: /\bsqlalchemy\b/gi, replacement: 'database' },
  { pattern: /\bpgvector\b/gi, replacement: 'database' },
  { pattern: /\bpostgresql?\b/gi, replacement: 'database' },
];

export function sanitizeError(message) {
  if (message == null) return 'An unexpected error occurred.';
  const str = typeof message === 'string' ? message : String(message);
  if (!str.trim()) return 'An unexpected error occurred.';

  if (BUSY_TRIGGERS.test(str)) return FRIENDLY_BUSY;
  if (UNSUPPORTED_MIMETYPE_TRIGGERS.test(str)) return FRIENDLY_UNSUPPORTED;

  let sanitized = str.replace(URL_STRIP, '');
  sanitized = sanitized.replace(DANGLING_TAILS, '');
  for (const { pattern, replacement } of TECH_PATTERNS) {
    sanitized = sanitized.replace(pattern, replacement);
  }
  sanitized = sanitized.replace(/\s+/g, ' ').trim();
  return sanitized || 'An unexpected error occurred.';
}
